import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFilePdf, FaFileCode, FaExpand } from 'react-icons/fa';
import type { InvoiceData, InvoiceItem } from '~/shared/types/invoice';
import type { InvoiceTemplate } from '~/shared/components/InvoiceTemplates';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { QRCodeSVG } from 'qrcode.react';

// ============================================================================
// INTERFACES
// ============================================================================

interface AdvancedInvoicePreviewProps {
  invoice: InvoiceData;
  selectedTemplate: InvoiceTemplate;
  uiLang: 'ar' | 'en';
  t: { preview: string; [key: string]: string | object };
  onExportPDF?: (data: InvoiceData) => void;
  onExportJSON?: (data: InvoiceData) => void;
  onTemplateChange?: () => void;
}

interface A4ContainerProps {
  children: React.ReactNode;
  width: number;
  height: number;
  scale: number;
  direction: 'rtl' | 'ltr';
  fontFamily: string;
  fontSize: string;
  lineHeight: number;
  color: string;
  className?: string;
}

// ============================================================================
// CONSTANTS - IDENTICAL TO CV PREVIEW
// ============================================================================

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

// ============================================================================
// A4 CONTAINER COMPONENT - IDENTICAL TO CV PREVIEW
// ============================================================================

const A4Container: React.FC<A4ContainerProps> = ({
  children,
  width,
  height,
  scale,
  direction,
  fontFamily,
  fontSize,
  lineHeight,
  color,
  className = ''
}) => {
  const containerStyle = useMemo(() => ({
    width: `${width}px`,
    height: `${height}px`,
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    position: 'relative' as const,
    fontFamily,
    fontSize,
    lineHeight,
    color,
    direction,
    textAlign: (direction === 'rtl' ? 'right' : 'left') as React.CSSProperties['textAlign'],
  }), [width, height, scale, direction, fontFamily, fontSize, lineHeight, color]);

  const contentStyle = useMemo(() => ({
    width: '100%',
    height: '100%',
    padding: '40px 32px',
    boxSizing: 'border-box' as const,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
  }), []);

  return (
    <div 
      className={`a4-container ${className}`}
      style={containerStyle}
      data-testid="a4-container"
    >
      <div className="a4-content" style={contentStyle}>
        {children}
      </div>
    </div>
  );
};

// ============================================================================
// INVOICE CONTENT RENDERER - SEPARATED FROM RESPONSIVE LAYOUT
// ============================================================================

const InvoiceContentRenderer: React.FC<{
  invoice: InvoiceData;
  template: InvoiceTemplate;
  uiLang: 'ar' | 'en';
  direction: 'rtl' | 'ltr';
}> = ({ invoice, template, uiLang, direction }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(uiLang === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(uiLang === 'ar' ? 'ar-SA' : 'en-US', {
      style: 'currency',
      currency: invoice.currency || 'SAR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const subtotal = invoice.items.reduce((sum: number, item: InvoiceItem) => sum + (item.quantity * item.price), 0);
  const taxAmount = (subtotal * invoice.taxRate) / 100;
  const total = subtotal + taxAmount - (invoice.discount || 0);

  const isInvoiceEmpty = !invoice.companyName && !invoice.clientName;

  if (isInvoiceEmpty) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full text-center text-gray-400 dark:text-gray-500">
        <svg className="w-16 h-16 mx-auto mb-4 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-lg font-bold mb-2">
          {uiLang === 'ar' ? 'ستظهر معاينة الفاتورة هنا' : 'Your invoice preview will appear here'}
        </h3>
        <p className="text-sm">
          {uiLang === 'ar' ? 'ابدأ بإدخال بيانات الشركة والعميل وستظهر المعاينة تلقائياً.' : 'Start entering company and client information and the preview will appear automatically.'}
        </p>
      </div>
    );
  }

  // استخراج خصائص القالب
  const { headerStyle, tableStyle, footerStyle, colorScheme } = template.layout;
  
  // ألوان ديناميكية محسنة لكل قالب
  const colorVars = {
    blue: {
      main: '#2563eb',
      bg: '#eff6ff',
      border: '#3b82f6',
      text: '#1e40af',
      accent: '#1d4ed8',
      light: '#dbeafe',
      dark: '#1e3a8a'
    },
    green: {
      main: '#059669',
      bg: '#ecfdf5',
      border: '#10b981',
      text: '#065f46',
      accent: '#047857',
      light: '#d1fae5',
      dark: '#064e3b'
    },
    purple: {
      main: '#7c3aed',
      bg: '#f5f3ff',
      border: '#a78bfa',
      text: '#4c1d95',
      accent: '#6d28d9',
      light: '#e9d5ff',
      dark: '#3c1f76'
    },
    orange: {
      main: '#f59e42',
      bg: '#fff7ed',
      border: '#fb923c',
      text: '#b45309',
      accent: '#ea580c',
      light: '#fed7aa',
      dark: '#9a3412'
    },
    gray: {
      main: '#6b7280',
      bg: '#f3f4f6',
      border: '#9ca3af',
      text: '#111827',
      accent: '#4b5563',
      light: '#e5e7eb',
      dark: '#374151'
    }
  };
  const palette = colorVars[colorScheme] || colorVars.blue;

  // أنماط الهيدر محسنة لكل قالب
  const getHeaderStyles = () => {
    switch (headerStyle) {
      case 'modern':
        return {
          className: 'invoice-header-modern',
          style: {
            background: `linear-gradient(135deg, ${palette.bg} 0%, ${palette.light} 100%)`,
            borderBottom: `4px solid ${palette.main}`,
            borderRadius: '12px 12px 0 0',
            padding: '24px 32px 20px 32px',
            margin: '0 -32px 24px -32px'
          }
        };
      case 'classic':
        return {
          className: 'invoice-header-classic',
          style: {
            borderBottom: `3px solid ${palette.border}`,
            background: '#ffffff',
            padding: '20px 0 16px 0'
          }
        };
      case 'minimal':
        return {
          className: 'invoice-header-minimal',
          style: {
            borderBottom: `1px solid ${palette.light}`,
            background: 'transparent',
            padding: '16px 0 12px 0'
          }
        };
      case 'corporate':
        return {
          className: 'invoice-header-corporate',
          style: {
            background: `linear-gradient(90deg, ${palette.main} 0%, ${palette.accent} 100%)`,
            color: '#ffffff',
            borderRadius: '8px',
            padding: '20px 24px 16px 24px',
            marginBottom: '24px',
            boxShadow: `0 4px 12px ${palette.main}20`
          }
        };
      default:
        return {
          className: 'invoice-header-default',
          style: {}
        };
    }
  };

  // أنماط الجدول محسنة لكل قالب
  const getTableStyles = () => {
    switch (tableStyle) {
      case 'clean':
        return {
          className: 'invoice-table-clean',
          style: {
            borderCollapse: 'collapse' as const,
            background: '#ffffff',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }
        };
      case 'bordered':
        return {
          className: 'invoice-table-bordered',
          style: {
            border: `2px solid ${palette.border}`,
            borderCollapse: 'collapse' as const,
            borderRadius: '8px',
            overflow: 'hidden'
          }
        };
      case 'striped':
        return {
          className: 'invoice-table-striped',
          style: {
            borderCollapse: 'collapse' as const,
            background: '#ffffff',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }
        };
      case 'elegant':
        return {
          className: 'invoice-table-elegant',
          style: {
            borderCollapse: 'collapse' as const,
            background: '#ffffff',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: `0 8px 24px ${palette.main}15`,
            border: `1px solid ${palette.light}`
          }
        };
      default:
        return {
          className: 'invoice-table-default',
          style: {}
        };
    }
  };

  // أنماط الفوتر محسنة لكل قالب
  const getFooterStyles = () => {
    switch (footerStyle) {
      case 'simple':
        return {
          className: 'invoice-footer-simple',
          style: {
            background: 'transparent',
            padding: '16px 0'
          }
        };
      case 'detailed':
        return {
          className: 'invoice-footer-detailed',
          style: {
            background: `linear-gradient(135deg, ${palette.bg} 0%, ${palette.light} 100%)`,
            borderTop: `3px solid ${palette.main}`,
            borderRadius: '8px',
            padding: '20px 24px',
            marginTop: '16px'
          }
        };
      case 'professional':
        return {
          className: 'invoice-footer-professional',
          style: {
            background: `linear-gradient(90deg, ${palette.main} 0%, ${palette.accent} 100%)`,
            color: '#ffffff',
            borderRadius: '12px',
            padding: '24px 32px',
            marginTop: '20px',
            boxShadow: `0 6px 20px ${palette.main}25`
          }
        };
      default:
        return {
          className: 'invoice-footer-default',
          style: {}
        };
    }
  };

  const headerStyles = getHeaderStyles();
  const tableStyles = getTableStyles();
  const footerStyles = getFooterStyles();

  // بيانات QR
  const qrValue = JSON.stringify({
    number: invoice.invoiceNumber,
    company: invoice.companyName,
    total: invoice.items.reduce((sum, item) => sum + (item.quantity * item.price), 0) + (invoice.items.reduce((sum, item) => sum + (item.quantity * item.price), 0) * invoice.taxRate / 100) - (invoice.discount || 0),
    date: invoice.invoiceDate
  });

  return (
    <div className="flex flex-col gap-6" style={{ direction, '--main-color': palette.main, '--bg-color': palette.bg, '--border-color': palette.border, '--text-color': palette.text } as React.CSSProperties}>
      {/* Header Section */}
      <div className={`invoice-header ${headerStyles.className} pb-6`} style={headerStyles.style}>
        <div className="flex justify-between items-start gap-6">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {uiLang === 'ar' ? 'فاتورة' : 'INVOICE'}
            </h1>
            <div className="space-y-1">
              <div className="font-semibold text-lg">{invoice.companyName}</div>
              <div className="text-gray-600 text-sm">{invoice.companyAddress}</div>
              <div className="text-gray-600 text-sm">{invoice.companyPhone}</div>
              <div className="text-gray-600 text-sm">{invoice.companyEmail}</div>
              {invoice.companyTaxNumber && (
                <div className="text-gray-600 text-sm">
                  {uiLang === 'ar' ? 'الرقم الضريبي:' : 'Tax Number:'} {invoice.companyTaxNumber}
                </div>
              )}
            </div>
          </div>
          {invoice.companyLogo && (
            <div className="flex-shrink-0">
              <img 
                src={invoice.companyLogo} 
                alt="Company Logo" 
                className="h-16 w-auto object-contain rounded-lg bg-gray-50 p-2"
              />
            </div>
          )}
        </div>
        
        <div className="flex flex-wrap gap-8 mt-4 text-sm">
          <div>
            <span className="font-semibold">{uiLang === 'ar' ? 'رقم الفاتورة:' : 'Invoice #:'}</span>
            <span className="ml-2">{invoice.invoiceNumber}</span>
          </div>
          <div>
            <span className="font-semibold">{uiLang === 'ar' ? 'التاريخ:' : 'Date:'}</span>
            <span className="ml-2">{formatDate(invoice.invoiceDate)}</span>
          </div>
          <div>
            <span className="font-semibold">{uiLang === 'ar' ? 'تاريخ الاستحقاق:' : 'Due Date:'}</span>
            <span className="ml-2">{formatDate(invoice.dueDate)}</span>
          </div>
        </div>
      </div>

      {/* Client and Shipping Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            {uiLang === 'ar' ? 'معلومات العميل' : 'Bill To'}
          </h2>
          <div className="space-y-1">
            <div className="font-medium">{invoice.clientName}</div>
            <div className="text-gray-600 text-sm">{invoice.clientAddress}</div>
            <div className="text-gray-600 text-sm">{invoice.clientPhone}</div>
            <div className="text-gray-600 text-sm">{invoice.clientEmail}</div>
          </div>
        </div>
        
        {(invoice.shippingName || invoice.shippingAddress) && (
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              {uiLang === 'ar' ? 'معلومات الشحن' : 'Ship To'}
            </h2>
            <div className="space-y-1">
              <div className="font-medium">{invoice.shippingName || invoice.clientName}</div>
              <div className="text-gray-600 text-sm">{invoice.shippingAddress || invoice.clientAddress}</div>
              {invoice.shippingPhone && (
                <div className="text-gray-600 text-sm">{invoice.shippingPhone}</div>
              )}
              {invoice.shippingEmail && (
                <div className="text-gray-600 text-sm">{invoice.shippingEmail}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Items Table */}
      {invoice.items.length > 0 && (
        <div>
          <table className={`invoice-table ${tableStyles.className}`} style={tableStyles.style}>
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 border-b border-gray-200 font-semibold text-gray-800">
                  {uiLang === 'ar' ? 'الوصف' : 'Description'}
                </th>
                <th className="text-center p-3 border-b border-gray-200 font-semibold text-gray-800">
                  {uiLang === 'ar' ? 'الكمية' : 'Qty'}
                </th>
                <th className="text-center p-3 border-b border-gray-200 font-semibold text-gray-800">
                  {uiLang === 'ar' ? 'السعر' : 'Price'}
                </th>
                <th className="text-center p-3 border-b border-gray-200 font-semibold text-gray-800">
                  {uiLang === 'ar' ? 'الإجمالي' : 'Total'}
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="p-3 border-b border-gray-100">
                    <div>
                      <div className="font-medium">{item.description}</div>
                      {item.notes && (
                        <div className="text-sm text-gray-600 mt-1">{item.notes}</div>
                      )}
                    </div>
                  </td>
                  <td className="p-3 border-b border-gray-100 text-center">{item.quantity}</td>
                  <td className="p-3 border-b border-gray-100 text-center">{formatCurrency(item.price)}</td>
                  <td className="p-3 border-b border-gray-100 text-center font-semibold">
                    {formatCurrency(item.price * item.quantity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Section with QR Code */}
      <div className={`flex justify-between items-start invoice-footer ${footerStyles.className}`} style={footerStyles.style}>
        {/* QR Code */}
        <div className="invoice-preview-qr">
          <QRCodeSVG
            value={qrValue}
            size={120}
            bgColor={palette.bg}
            fgColor={palette.main}
            level="M"
            includeMargin={true}
          />
        </div>

        {/* Summary Section */}
        <div className="w-80 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">{uiLang === 'ar' ? 'المجموع الفرعي:' : 'Subtotal:'}</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">
              {uiLang === 'ar' ? `الضريبة (${invoice.taxRate}%):` : `Tax (${invoice.taxRate}%):`}
            </span>
            <span>{formatCurrency(taxAmount)}</span>
          </div>
          {invoice.discount > 0 && (
            <div className="flex justify-between">
              <span className="text-red-600">{uiLang === 'ar' ? 'الخصم:' : 'Discount:'}</span>
              <span className="text-red-600">-{formatCurrency(invoice.discount)}</span>
            </div>
          )}
          <div className="border-t-2 border-gray-200 pt-3">
            <div className="flex justify-between text-lg font-bold text-blue-600">
              <span>{uiLang === 'ar' ? 'المجموع الكلي:' : 'Total:'}</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes and Terms */}
      {(invoice.notes || invoice.paymentTerms) && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="space-y-2">
            {invoice.notes && (
              <div>
                <span className="font-semibold">{uiLang === 'ar' ? 'ملاحظات:' : 'Notes:'}</span>
                <span className="ml-2 text-gray-700">{invoice.notes}</span>
              </div>
            )}
            {invoice.paymentTerms && (
              <div>
                <span className="font-semibold">{uiLang === 'ar' ? 'شروط الدفع:' : 'Payment Terms:'}</span>
                <span className="ml-2 text-gray-700">{invoice.paymentTerms}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT - IDENTICAL LAYOUT TO CV PREVIEW
// ============================================================================

const AdvancedInvoicePreview: React.FC<AdvancedInvoicePreviewProps> = ({
  invoice,
  selectedTemplate,
  uiLang,
  t,
  onExportPDF,
  onExportJSON,
  onTemplateChange
}) => {
  // ===== STATE MANAGEMENT - IDENTICAL TO CV PREVIEW =====
  const [scale, setScale] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // ===== COMPUTED VALUES - IDENTICAL TO CV PREVIEW =====
  const direction = uiLang === 'ar' ? 'rtl' : 'ltr';
  const fontFamily = uiLang === 'ar' ? 'Tajawal, Arial, sans-serif' : 'Inter, Arial, sans-serif';
  const fontSize = '14px';
  const lineHeight = 1.6;
  const color = '#374151';

  // ===== RESPONSIVE SCALING - IDENTICAL TO CV PREVIEW =====
  useEffect(() => {
    const updateResponsiveSize = () => {
      if (!containerRef.current) return;
      setScale(1);
    };
    updateResponsiveSize();
    window.addEventListener('resize', updateResponsiveSize);
    return () => window.removeEventListener('resize', updateResponsiveSize);
  }, []);

  // ===== FIXED A4 SIZING WITH AUTO SCALE - IDENTICAL TO CV PREVIEW =====
  const MAX_SCALE = isMobile ? 1 : 0.95;
  const MIN_MARGIN = isMobile ? 0 : 24;
  const MAX_WIDTH = isMobile ? '100vw' : '75vw';
  const scaledWidth = A4_WIDTH * scale;
  const scaledHeight = A4_HEIGHT * scale;

  useEffect(() => {
    function updateScale() {
      if (!containerRef.current) return;
      let containerW;
      if (window.innerWidth < 640) {
        containerW = window.innerWidth;
      } else {
        containerW = containerRef.current.offsetWidth;
      }
      const scaleW = (containerW - 2 * MIN_MARGIN) / A4_WIDTH;
      setScale(Math.min(scaleW, MAX_SCALE));
    }
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [MIN_MARGIN, MAX_SCALE]);

  // ===== EXPORT HANDLERS - IDENTICAL TO CV PREVIEW =====
  const handleExportJSON = useCallback(() => {
    if (!onExportJSON) return;
    
    setIsExporting(true);
    try {
      onExportJSON(invoice);
    } catch (error) {
      console.error('JSON export failed:', error);
    } finally {
      setIsExporting(false);
    }
  }, [onExportJSON, invoice]);

  const handleAdvancedExportPDF = useCallback(async () => {
    setIsExporting(true);
    try {
      const element = containerRef.current?.querySelector('[data-testid="a4-container"]') as HTMLElement;
      if (!element) {
        throw new Error('Preview element not found');
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#fff',
        width: element.offsetWidth,
        height: element.offsetHeight,
        logging: false,
        imageTimeout: 15000,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const pxPerMm = canvas.width / imgWidth;
      const pageHeightPx = Math.floor(pageHeight * pxPerMm);
      const marginPx = 40;
      const pageCount = Math.ceil(canvas.height / (pageHeightPx - marginPx));

      for (let i = 0; i < pageCount; i++) {
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = pageHeightPx - marginPx;
        const ctx = pageCanvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(
            canvas,
            0, i * (pageHeightPx - marginPx),
            canvas.width, pageHeightPx - marginPx,
            0, 0,
            canvas.width, pageHeightPx - marginPx
          );
        }
        const imgData = pageCanvas.toDataURL('image/png', 1.0);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, pageHeight - (marginPx / pxPerMm));
      }

      const fileName = `invoice_${invoice.invoiceNumber || 'invoice'}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      if (onExportPDF) {
        onExportPDF(invoice);
      }
      
      alert(uiLang === 'ar' ? 'تم تصدير PDF بنجاح!' : 'PDF exported successfully!');
    } catch (error) {
      console.error('PDF export failed:', error);
      alert(uiLang === 'ar' ? 'فشل في تصدير PDF: ' + (error instanceof Error ? error.message : 'خطأ غير معروف') : 'Failed to export PDF: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsExporting(false);
    }
  }, [invoice, uiLang, onExportPDF]);

  // ===== TEMPLATE CHANGE HANDLER - IDENTICAL TO CV PREVIEW =====
  const handleTemplateChange = useCallback(() => {
    if (onTemplateChange) {
      onTemplateChange();
    }
  }, [onTemplateChange]);

  // ===== RENDER - IDENTICAL LAYOUT TO CV PREVIEW =====
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full flex flex-col items-center"
      style={{ minHeight: '100vh' }}
    >
      {/* Header - IDENTICAL TO CV PREVIEW */}
      <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white mb-4 sm:mb-6 text-center">
        {t.preview}
      </h2>

      {/* Controls - IDENTICAL TO CV PREVIEW */}
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 mb-4 sm:mb-6">
        <button
          onClick={handleTemplateChange}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          title={uiLang === 'ar' ? 'تغيير القالب' : 'Change Template'}
        >
          <FaExpand />
          <span className="hidden sm:inline">
            {uiLang === 'ar' ? 'تغيير القالب' : 'Change Template'}
          </span>
        </button>

        <button
          onClick={handleAdvancedExportPDF}
          disabled={isExporting}
          className="flex items-center gap-2 px-3 py-2 bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 transition-colors text-sm disabled:opacity-50"
          title={uiLang === 'ar' ? 'تصدير PDF احترافي' : 'Export Professional PDF'}
        >
          <FaFilePdf />
          <span className="hidden sm:inline">
            {uiLang === 'ar' ? 'تصدير PDF احترافي' : 'Export Professional PDF'}
          </span>
        </button>

        <button
          onClick={handleExportJSON}
          disabled={isExporting}
          className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm disabled:opacity-50"
          title={uiLang === 'ar' ? 'تصدير JSON' : 'Export JSON'}
        >
          <FaFileCode />
          <span className="hidden sm:inline">
            {uiLang === 'ar' ? 'تصدير JSON' : 'Export JSON'}
          </span>
        </button>
      </div>

      {/* Preview Container - IDENTICAL TO CV PREVIEW */}
      <div
        ref={containerRef}
        className="flex justify-center items-center"
        style={{
          width: isMobile ? '100vw' : '100%',
          height: isMobile ? `${scaledHeight}px` : '100%',
          minHeight: 0,
          padding: 0,
          margin: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: isMobile ? 'auto' : 'hidden',
          boxSizing: 'border-box',
          background: 'transparent',
        }}
      >
        {/* غلاف الورقة المصغرة مع margin تلقائي - IDENTICAL TO CV PREVIEW */}
        <div
          style={{
            width: `${scaledWidth}px`,
            height: `${scaledHeight}px`,
            position: 'relative',
            pointerEvents: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: `${MIN_MARGIN}px`,
            boxSizing: 'border-box',
            maxWidth: MAX_WIDTH,
          }}
        >
          {/* الورقة الأصلية مع التصغير - IDENTICAL TO CV PREVIEW */}
          <div
            style={{
              width: `${A4_WIDTH}px`,
              height: `${A4_HEIGHT}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              boxShadow: '0 12px 48px 0 rgba(124, 58, 237, 0.10)',
              borderRadius: '2rem',
              background: '#fff',
              position: 'absolute',
              top: 0,
              left: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              userSelect: 'none',
            }}
          >
            <A4Container
              width={A4_WIDTH}
              height={A4_HEIGHT}
              scale={1}
              direction={direction}
              fontFamily={fontFamily}
              fontSize={fontSize}
              lineHeight={lineHeight}
              color={color}
              className={'custom-a4-shadow custom-a4-rounded'}
            >
              <InvoiceContentRenderer
                invoice={invoice}
                template={selectedTemplate}
                uiLang={uiLang}
                direction={direction}
              />
            </A4Container>
          </div>
        </div>
      </div>

      {/* A4 Indicator - IDENTICAL TO CV PREVIEW */}
      <div className="mt-8 mb-8 text-center px-2">
        <div className="inline-flex items-center gap-1 sm:gap-2 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 text-blue-700 dark:text-blue-400 px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium shadow-sm border border-blue-200 dark:border-blue-800">
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <span className="hidden md:inline">A4 Format (210mm × 297mm)</span>
          <span className="hidden sm:inline md:hidden">A4 Format</span>
          <span className="sm:hidden">A4</span>
        </div>
      </div>

      {/* Loading Overlay - IDENTICAL TO CV PREVIEW */}
      <AnimatePresence>
        {isExporting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center gap-4">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              <span className="text-gray-700 dark:text-gray-300">
                {uiLang === 'ar' ? 'جاري تصدير PDF...' : 'Exporting PDF...'}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AdvancedInvoicePreview; 